import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './Footer.css';

const COLLECTIONS = [
  { to: '/catalog?size=12x24', label: 'Porcelain 12×24' },
  { to: '/catalog?size=24x48', label: 'Porcelain 24×48' },
  { to: '/catalog?size=48x72', label: 'Porcelain 48×72' },
  { to: '/catalog?category=woodLook', label: 'Wood Look 8×48' },
];

const COMPANY_LINKS = [
  { to: '/',        label: 'Home'    },
  { to: '/catalog', label: 'Catalog' },
  { to: '/about',   label: 'About'   },
  { to: '/contact', label: 'Contact' },
];

const SOCIALS = [
  {
    label: 'Instagram',
    href: 'https://instagram.com',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    label: 'Pinterest',
    href: 'https://pinterest.com',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.65 7.86 6.39 9.29-.09-.78-.17-1.98.04-2.83.18-.76 1.23-5.22 1.23-5.22s-.31-.63-.31-1.56c0-1.46.85-2.55 1.9-2.55.9 0 1.33.67 1.33 1.48 0 .9-.58 2.26-.87 3.52-.25 1.05.52 1.91 1.55 1.91 1.86 0 3.11-2.39 3.11-5.22 0-2.15-1.45-3.66-3.52-3.66-2.4 0-3.81 1.8-3.81 3.66 0 .73.28 1.5.63 1.93.07.08.08.16.06.24l-.23.94c-.04.15-.13.18-.29.11-1.07-.5-1.74-2.08-1.74-3.35 0-2.72 1.98-5.22 5.7-5.22 2.99 0 5.32 2.13 5.32 4.98 0 2.97-1.87 5.35-4.47 5.35-.87 0-1.69-.45-1.97-1l-.54 2c-.19.74-.72 1.67-1.07 2.23.8.25 1.65.39 2.52.39 5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
        <rect x="2" y="9" width="4" height="12"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    ),
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState('idle'); // idle | sending | success | error

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubStatus('sending');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name:    'Newsletter Signup',
          from_email:   email.trim(),
          message:      `New newsletter subscriber: ${email.trim()}`,
          project_type: 'Newsletter',
          tile_interest: '',
          from_phone:   '',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setSubStatus('success');
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <footer className="footer">
      {/* Newsletter bar */}
      <div className="footer__newsletter">
        <div className="footer__newsletter-inner">
          <div className="footer__newsletter-text">
            <p className="footer__newsletter-label">Stay Inspired</p>
            <p className="footer__newsletter-sub">New collections, design ideas, and project showcases — in your inbox.</p>
          </div>
          {subStatus === 'success' ? (
            <p className="footer__newsletter-thanks">Thank you — we'll be in touch.</p>
          ) : (
            <form className="footer__newsletter-form" onSubmit={handleSubscribe}>
              <input
                type="email"
                className="footer__newsletter-input"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={subStatus === 'sending'}
                required
              />
              <button type="submit" className="footer__newsletter-btn" disabled={subStatus === 'sending'}>
                {subStatus === 'sending' ? 'Sending…' : 'Subscribe'}
              </button>
              {subStatus === 'error' && (
                <p className="footer__newsletter-error">Something went wrong — please try again.</p>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Main columns */}
      <div className="footer__main">
        {/* Brand */}
        <div className="footer__brand">
          <img src="/assets/images/arden-logo-white.png" alt="Arden Surfaces" className="footer__logo-img" />
          <p className="footer__tagline">
            Premium tile distribution for residential and commercial spaces.
            Factory-direct from Dallas, Texas since 2026.
          </p>
          <div className="footer__socials">
            {SOCIALS.map(({ label, href, icon }) => (
              <a key={label} href={href} className="footer__social-link" aria-label={label} target="_blank" rel="noopener noreferrer">
                {icon}
              </a>
            ))}
          </div>
        </div>

        {/* Collections */}
        <div>
          <h4 className="footer__heading">Collections</h4>
          <ul className="footer__links">
            {COLLECTIONS.map(({ to, label }) => (
              <li key={to}><Link to={to}>{label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="footer__heading">Company</h4>
          <ul className="footer__links">
            {COMPANY_LINKS.map(({ to, label }) => (
              <li key={to}><Link to={to}>{label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="footer__contact">
          <h4 className="footer__heading">Contact Us</h4>
          <div className="footer__contact-item">
            <span className="footer__contact-label">Showroom</span>
            <span className="footer__contact-value">201 Range Drive, Suite E<br />Garland, TX 75040</span>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-label">Phone</span>
            <span className="footer__contact-value">
              <a href="tel:+12146776811">(214) 677-6811</a><br />
              <a href="tel:+12146067371">(214) 606-7371</a>
            </span>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-label">Email</span>
            <span className="footer__contact-value">
              <a href="mailto:inquiries@ardensurfaces.com">inquiries@ardensurfaces.com</a>
            </span>
          </div>
          <div className="footer__contact-item">
            <span className="footer__contact-label">Hours</span>
            <span className="footer__contact-value">Mon–Sat &nbsp;9 am – 6 pm</span>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer__bottom">
        <p className="footer__copyright">&copy; 2026 Arden Surfaces. All rights reserved.</p>
        <p className="footer__copyright">Garland, Texas &nbsp;·&nbsp; inquiries@ardensurfaces.com</p>
      </div>
    </footer>
  );
}
